import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import SwitchToBackButton from "../../reuseableComponents/SwitchToBackButton";
import useCopyToStoreList from "../../hooks/useCopyToStoreList";
import BasicTextFields from "../../reuseableComponents/TextInputField";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { useAuthDetails } from "../../Common/cookiesHelper";
import { useDispatch } from "react-redux";
import PasswordShow from "../../Common/passwordShow";
import {
  buildDiscountPriceConflictMessage,
  getItemsPricedBelowDiscount,
  handleInputNumber,
  isValidNumber,
} from "../../Constants/utils";
import { addNewDeal } from "../../Redux/features/MixAndMatch/mixAndMatchSlice";
import { ToastifyAlert } from "../../CommonComponents/ToastifyAlert";
import SearchableDropdownForBogo from "../Bogo/SearchableDropdownForBogo";
import BogoProductSelectionModal from "../Bogo/BogoProductSelectionModal";
import HoverInfoLogo from "../../reuseableComponents/HoverInfoLogo";
import InputTextSearch from "../../reuseableComponents/InputTextSearch";
import CloseIcon from "../../Assests/Dashboard/cross.svg";
import { withReactQueryBoundary } from "../../lib/react-query";
import "../../Styles/PromotionsWorkspace.css";

const AddMixAndMatchDeal = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isPending, startTransition] = useTransition();
  const { userTypeData, LoginGetDashBoardRecordJson, LoginAllStore } =
    useAuthDetails();
  const merchant_id = LoginGetDashBoardRecordJson?.data?.merchant_id;
  const availableStoreRecords =
    (Array.isArray(LoginGetDashBoardRecordJson?.stores) &&
      LoginGetDashBoardRecordJson?.stores) ||
    (Array.isArray(LoginGetDashBoardRecordJson?.data?.stores) &&
      LoginGetDashBoardRecordJson?.data?.stores) ||
    (Array.isArray(LoginAllStore?.data?.stores) && LoginAllStore?.data?.stores) ||
    (Array.isArray(LoginAllStore?.data2?.stores) &&
      LoginAllStore?.data2?.stores) ||
    [];
  const selectedStoreRecord = availableStoreRecords.find(
    (store) => store?.merchant_id === merchant_id || store?.id === merchant_id
  );
  const enabledTobaccoScanProgramsRaw =
    LoginGetDashBoardRecordJson?.data?.enabled_tobacco_scan_programs ??
    selectedStoreRecord?.enabled_tobacco_scan_programs ??
    "";
  const enabledTobaccoScanPrograms =
    typeof enabledTobaccoScanProgramsRaw === "string"
      ? enabledTobaccoScanProgramsRaw
          .split(",")
          .map((item) => item.trim().toLowerCase())
          .filter(Boolean)
      : [];
  const hasTobaccoScanProgramAccess = enabledTobaccoScanPrograms.length > 0;
  const { getUnAutherisedTokenMessage, handleCoockieExpire, getNetworkError } =
    PasswordShow();

  const [loading, setLoading] = useState(false);
  const submitInFlightRef = useRef(false);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [modalError, setModalError] = useState("");
  const [searchVariantList, setSearchVariantList] = useState("");
  const [displayedProducts, setDisplayedProducts] = useState([]);

  // other stores of this merchant (superadmin: the viewed merchant's stores)
  const getStoreList = useCopyToStoreList();
  const [promiseLoading] = useState(false);
  const [dropdownData, setDropdowndata] = useState({
    copyToStoreList: [],
  });
  const [productInfo, setProductInfo] = useState({
    copyToStore: [],
  });

  const [dealInfo, setDealInfo] = useState({
    title: "",
    description: "",
    products: [],
    minQty: "0",
    maxQty: null,
    discount: "0.00",
    isPercent: "0",
    altriaFundedDiscount: 0,
    promotionCode: "",
  });

  const [error, setError] = useState({
    title: "",
    products: "",
    minQty: "",
    maxQty: "",
    discount: "",
  });

  const activeTab = dealInfo.isPercent === "1" ? "percentage" : "amount";
  const hasSelectedProducts = dealInfo.products?.length > 0;

  // A flat discount that meets or beats an item's price would ring it up free.
  // The picker hides those rows at selection time, so the way in is raising the
  // discount after products were chosen. Recomputed live as the merchant types.
  const discountPriceConflicts = useMemo(
    () =>
      activeTab === "amount"
        ? getItemsPricedBelowDiscount(dealInfo.products, dealInfo.discount)
        : [],
    [activeTab, dealInfo.products, dealInfo.discount]
  );
  const discountPriceConflictMessage = useMemo(
    () => buildDiscountPriceConflictMessage(discountPriceConflicts),
    [discountPriceConflicts]
  );
  const conflictingProductKeys = useMemo(
    () =>
      new Set(
        discountPriceConflicts.map((item) =>
          item?.isvarient === "1" ? `v:${item?.var_id}` : `p:${item?.id}`
        )
      ),
    [discountPriceConflicts]
  );
  const isConflictingProduct = (product) =>
    conflictingProductKeys.has(
      product?.isvarient === "1" ? `v:${product?.var_id}` : `p:${product?.id}`
    );

  useEffect(() => {
    setError((prev) => {
      if (discountPriceConflictMessage) {
        if (prev.discountPriceConflict === discountPriceConflictMessage) {
          return prev;
        }
        return { ...prev, discountPriceConflict: discountPriceConflictMessage };
      }
      if (!prev.discountPriceConflict) return prev;
      const { discountPriceConflict, ...rest } = prev;
      return rest;
    });
  }, [discountPriceConflictMessage]);

  // Keep chip list in sync with selected products
  useEffect(() => {
    setDisplayedProducts(dealInfo.products || []);
    setSearchVariantList("");
  }, [dealInfo.products]);

  useEffect(() => {
    const description = `Buy ${dealInfo.minQty} get ${
      dealInfo.isPercent === "1"
        ? `${dealInfo.discount}%`
        : `$${dealInfo.discount}`
    } off each`;
    setDealInfo((prev) => ({ ...prev, description }));
  }, [dealInfo.discount, dealInfo.isPercent, dealInfo.minQty]);

  useEffect(() => {
    setDropdowndata((prev) => ({
      ...prev,
      copyToStoreList: getStoreList?.length
        ? [{ id: "111", name: "Select All" }, ...getStoreList]
        : [],
    }));
  }, [getStoreList]);

  const handleDeleteSelectedOption = (product) => {
    setDealInfo((prev) => ({
      ...prev,
      products: prev.products.filter((item) =>
        item.isvarient === "1"
          ? item.var_id !== product.var_id
          : item?.id !== product?.id
      ),
    }));
  };

  const handleVariantListSearch = (value) => {
    const lowerVal = value.toLowerCase();
    const updateData = (dealInfo.products || []).filter(
      (i) =>
        i?.title?.toLowerCase().includes(lowerVal) ||
        i?.upc?.toLowerCase().includes(lowerVal) ||
        i?.var_upc?.toLowerCase().includes(lowerVal) ||
        i?.custom_code?.toLowerCase()?.includes(lowerVal) ||
        i?.var_custom_code?.toLowerCase()?.includes(lowerVal)
    );
    setSearchVariantList(value);
    setDisplayedProducts(updateData);
  };

  const handleOpenProductModal = () => {
    setError((prev) => {
      const { products, discount, ...rest } = prev;
      return rest;
    });
    setModalError("");

    if (activeTab === "amount") {
      if (
        !dealInfo.discount ||
        dealInfo.discount === "0.00" ||
        parseFloat(dealInfo.discount) <= 0
      ) {
        setError((prev) => ({
          ...prev,
          discount: "Discount per item is required",
        }));
        return;
      }
    } else if (
      !dealInfo.discount ||
      dealInfo.discount === "0" ||
      parseFloat(dealInfo.discount) <= 0
    ) {
      setError((prev) => ({
        ...prev,
        discount: "Discount per item is required",
      }));
      return;
    }

    startTransition(() => setProductModalOpen(true));
  };

  const handleProductSelectionConfirm = (nextSelections) => {
    const selections = Array.isArray(nextSelections) ? nextSelections : [];
    setDealInfo((prev) => ({
      ...prev,
      products: selections,
    }));
    setError((prev) => {
      const { products, ...rest } = prev;
      return rest;
    });
    setModalError("");
    startTransition(() => setProductModalOpen(false));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDealInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleAltriaFundedDiscountToggle = (event) => {
    const isFunded = event.target.checked;
    setDealInfo((prev) => ({
      ...prev,
      altriaFundedDiscount: isFunded ? 1 : 0,
      promotionCode: isFunded ? prev.promotionCode : "",
    }));
  };

  const handleTabChange = (type) => {
    setDealInfo((prev) => ({
      ...prev,
      isPercent: type === "amount" ? "0" : "1",
      discount: type === "amount" ? "0.00" : "0",
    }));
  };

  const handleAddNewDeal = async (e) => {
    try {
      e.preventDefault();
      if (submitInFlightRef.current || loading) {
        return;
      }
      // Over-discounted products block the save until the merchant lowers the
      // discount or removes them.
      if (discountPriceConflictMessage) {
        setError((prev) => ({
          ...prev,
          discountPriceConflict: discountPriceConflictMessage,
        }));
        return;
      }
      submitInFlightRef.current = true;
      setLoading(true);
      const {
        title,
        products,
        minQty,
        maxQty,
        discount,
        isPercent,
        description,
        altriaFundedDiscount,
        promotionCode,
      } = dealInfo;

      const isMaxQtyEntered =
        maxQty !== null && maxQty !== "" && maxQty !== undefined;
      const isMaxQtyValid =
        !isMaxQtyEntered || Number(maxQty) >= Number(minQty);

      if (
        Boolean(title.trim()) &&
        minQty > 0 &&
        parseFloat(discount) > 0 &&
        products.length > 0 &&
        isMaxQtyValid
      ) {
        try {
          const items = {};
          products.forEach((item) => {
            if (item.isvarient === "1") {
              if (items[item.id]) {
                items[item.id] = [...items[item.id], item.var_id];
              } else {
                items[item.id] = [item.var_id];
              }
            } else {
              items[item.id] = [""];
            }
          });

          const data = {
            merchant_id: LoginGetDashBoardRecordJson?.data?.merchant_id,
            deal_name: title,
            min_qty: minQty,
            max_qty: maxQty ?? null,
            description,
            discount,
            is_percent: isPercent,
            items_id: JSON.stringify(items),
            is_enable: 1,
            altria_funded_discount: Number(altriaFundedDiscount ?? 0),
            promotion_code: Number(altriaFundedDiscount ?? 0)
              ? String(promotionCode ?? "").trim()
              : "",
            mix_id: "",
            stores:
              productInfo?.copyToStore
                ?.map((i) => i.merchant_id)
                .join(",")
                .trim() ?? "",
            ...userTypeData,
          };
          const result = await dispatch(addNewDeal(data)).unwrap();

          if (result) {
            ToastifyAlert("Added Successfully", "success");
            navigate("/promotions/mix-and-match");
          } else {
            ToastifyAlert("Something went wrong!", "error");
          }
        } catch (err) {
          if (err?.status == 401 || err?.response?.status === 401) {
            getUnAutherisedTokenMessage();
            handleCoockieExpire();
          } else if (err?.status == "Network Error") {
            getNetworkError();
          }
        }
      } else {
        setError((prev) => ({
          ...prev,
          title: !title ? "Deal name is required!" : "",
          products: products.length <= 0 ? "Products are required!" : "",
          minQty: !minQty || minQty <= 0 ? "Minimum quantity is required!" : "",
          maxQty: !isMaxQtyValid
            ? "Maximum quantity must be greater than or equal to minimum quantity!"
            : "",
          discount:
            !discount || parseFloat(discount) <= 0
              ? "Discount per item is required"
              : "",
        }));
      }
    } catch (err) {
      console.log("error: ", err);
    } finally {
      submitInFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleSelectProductOptions2 = (value, name) => {
    if (name === "copyToStore" && value?.name === "Select All") {
      if (productInfo?.copyToStore?.length === getStoreList?.length) {
        setProductInfo((prev) => ({
          ...prev,
          [name]: [],
        }));
      } else if (!productInfo?.copyToStore?.length) {
        setProductInfo((prev) => ({
          ...prev,
          [name]: [...prev[name], ...getStoreList],
        }));
      } else {
        const filteredData = getStoreList?.filter(
          (o) =>
            !productInfo.copyToStore.some(
              (i) => i.id.toLowerCase() === o.id.toLowerCase()
            )
        );
        setProductInfo((prev) => ({
          ...prev,
          [name]: [...prev[name], ...filteredData],
        }));
      }
    } else {
      setProductInfo((prev) => ({
        ...prev,
        [name]: [...prev[name], value],
      }));
    }
  };

  const handleDeleteSelectedOption2 = (id, name) => {
    setProductInfo((prev) => ({
      ...prev,
      [name]: prev[name].filter((item) => item?.id !== id),
    }));
  };

  return (
    <>
      <div className="box qv-promo-workspace qv-promo-form qv-mix-workspace">
        <div className="box_shadow_div">
          <div className="q-add-categories-section">
            <SwitchToBackButton
              linkTo={"/promotions/mix-and-match"}
              title={"Add Deal"}
            />
            <form
              className="mb-14 sm:mb-0"
              onSubmit={(e) => {
                // Prevent barcode-scanner Enter from submitting the form.
                e.preventDefault();
              }}
            >
              <Grid
                sx={{
                  px: { xs: 1, sm: 2.5 },
                  display: "flex",
                  flexDirection: "column",
                }}
                className="q-add-categories-section-middle-form"
              >
                <div className="q-add-coupon-single-input mb-4">
                  <label htmlFor="coupon_name">Deal Name</label>
                  <div className="input_area" style={{ marginBottom: "0px" }}>
                    <BasicTextFields
                      type={"text"}
                      maxLength={100}
                      value={dealInfo.title}
                      name="title"
                      onChangeFun={handleInputChange}
                    />
                  </div>
                  {error.title && (
                    <p className="error-message">{error.title}</p>
                  )}
                </div>
                <div className="q-add-coupon-single-input mb-6">
                  <label htmlFor="description">Description</label>
                  <div
                    className="input_area read-only-input-field"
                    style={{ marginBottom: "0px" }}
                  >
                    <BasicTextFields
                      type={"text"}
                      value={dealInfo.description}
                      name="description"
                      readOnly={true}
                    />
                  </div>
                </div>

                {dropdownData?.copyToStoreList?.length &&
                dropdownData?.copyToStoreList?.length > 1 ? (
                  <div className="q-add-categories-single-input mb-6">
                    <SearchableDropdownForBogo
                      title="Copy deal to other stores"
                      keyName="copyToStore"
                      name="name"
                      optionList={dropdownData?.copyToStoreList}
                      handleSelectProductOptions={handleSelectProductOptions2}
                      handleDeleteSelectedOption={handleDeleteSelectedOption2}
                      selectedOption={productInfo?.copyToStore}
                      error={error}
                      placeholder="Search Stores"
                      productTitle={productInfo?.title}
                      allowedAllSelect={true}
                      optionsLoading={promiseLoading}
                    />
                  </div>
                ) : (
                  ""
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <div className="q_coupon_minium input_area">
                      <label htmlFor="minorder_amt">Minimum Quantity</label>
                      <BasicTextFields
                        type={"text"}
                        inputMode="numeric"
                        maxLength={6}
                        value={dealInfo.minQty}
                        onChangeFun={(e) => {
                          if (isValidNumber(Number(e.target.value))) {
                            handleInputNumber(e, setDealInfo, dealInfo);
                          }
                        }}
                        placeholder="Enter Minimum Quantity"
                        name="minQty"
                      />
                      {error.minQty && (
                        <p className="error-message">{error.minQty}</p>
                      )}
                    </div>
                    <div className="q_coupon_minium input_area mt-4">
                      <label htmlFor="maxorder_amt">Maximum Quantity</label>
                      <BasicTextFields
                        type={"text"}
                        inputMode="numeric"
                        maxLength={6}
                        value={dealInfo.maxQty ?? ""}
                        onChangeFun={(e) => {
                          if (isValidNumber(Number(e.target.value))) {
                            handleInputNumber(e, setDealInfo, dealInfo);
                          }
                        }}
                        placeholder="Enter Maximum Quantity"
                        name="maxQty"
                      />
                      {error.maxQty && (
                        <p className="error-message">{error.maxQty}</p>
                      )}
                    </div>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <label htmlFor="discount_amt">
                      Discount Per Item (
                      {dealInfo.isPercent === "1" ? "%" : "$"})
                    </label>
                    <div className="q_coupon_minium  dicount_per_amo">
                      <Grid container>
                        <Grid item xs={12} sm={6} md>
                          <div
                            style={{ marginBottom: "0" }}
                            className="q_coupon_minium input_area"
                          >
                            <BasicTextFields
                              type={"text"}
                              inputMode="numeric"
                              maxLength={9}
                              value={dealInfo.discount}
                              onChangeFun={(e) => {
                                handleInputNumber(e, setDealInfo, dealInfo);
                              }}
                              placeholder="Enter Discount Amount"
                              name="discount"
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} sm={6} md={7}>
                          <div className="AMT_PER_button">
                            <Grid container>
                              <Grid item xs={6}>
                                <div
                                  className={`cursor-pointer amt_btn text-center   ${
                                    dealInfo.isPercent === "0"
                                      ? "bg-[#0A64F9] text-white radius-4"
                                      : ""
                                  }`}
                                  onClick={() => handleTabChange("amount")}
                                >
                                  Amount ($)
                                </div>
                              </Grid>
                              <Grid item xs={6}>
                                <div
                                  className={`cursor-pointer amt_btn text-center  ${
                                    dealInfo.isPercent === "1"
                                      ? "bg-[#0A64F9] text-white radius-4"
                                      : ""
                                  }`}
                                  style={{ whiteSpace: "nowrap" }}
                                  onClick={() => handleTabChange("percentage")}
                                >
                                  Percentage (%)
                                </div>
                              </Grid>
                            </Grid>
                          </div>
                        </Grid>
                      </Grid>
                    </div>
                    {error.discount && (
                      <p className="error-message">{error.discount}</p>
                    )}
                    {error.discountPriceConflict && (
                      <p className="error-message">
                        {error.discountPriceConflict}
                      </p>
                    )}
                  </Grid>

                  {hasSelectedProducts ? (
                    <Grid item xs={12}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        alignItems={"center"}
                      >
                        <Grid item>
                          <p>Variants Included In Offer</p>
                        </Grid>
                        <Grid
                          item
                          className="cursor-pointer amt_btn text-center text-[#0a64f9] radius-4 flex py-6"
                        >
                          <div className="relative flex justify-end">
                            <p
                              className="select-none"
                              onClick={handleOpenProductModal}
                            >
                              Add Variants+
                            </p>
                            <p className="absolute -bottom-2/4 end-0">
                              {error?.products && (
                                <p className="error-message">
                                  {error.products}
                                </p>
                              )}
                            </p>
                          </div>
                        </Grid>
                      </Grid>
                      <div className="qv-promo-selected-variants">
                        <div className="inpuxBox">
                          <InputTextSearch
                            placeholder="Search selected products by name, UPC, or custom code"
                            value={searchVariantList}
                            handleChange={handleVariantListSearch}
                          />
                        </div>
                        {displayedProducts?.length > 0 ? (
                          <div className="flex flex-wrap gap-3 max-h-56 overflow-auto">
                            {displayedProducts.map((variant, index) => (
                              <div
                                key={
                                  variant?.isvarient === "1"
                                    ? variant?.var_id
                                    : variant?.id || index
                                }
                                className={`border p-2 flex gap-2 items-center ${
                                  isConflictingProduct(variant)
                                    ? "border-[#d32f2f] text-[#d32f2f]"
                                    : "border-[#ccc]"
                                }`}
                              >
                                <div>{variant?.title}</div>
                                <img
                                  src={CloseIcon}
                                  alt=""
                                  className="cancel-image"
                                  onClick={() =>
                                    handleDeleteSelectedOption(variant)
                                  }
                                />
                              </div>
                            ))}
                          </div>
                        ) : searchVariantList &&
                          (dealInfo.products || []).length > 0 ? (
                          <p className="qv-promo-selected-variants__empty">
                            No matching products.
                          </p>
                        ) : null}
                      </div>
                    </Grid>
                  ) : (
                    <Grid item md={6} xs={12}>
                      <div
                        className="cursor-pointer amt_btn text-center bg-[#0A64F9] text-white radius-4 flex relative py-5"
                        onClick={handleOpenProductModal}
                      >
                        <div className="flex gap-2">
                          <p className="flex">
                            Add Variant +
                            {isPending ? (
                              <Box className="loader-box">
                                <CircularProgress disableShrink={true} />
                              </Box>
                            ) : null}
                          </p>
                        </div>
                        <div className="justify-self-end absolute end-2 top-1/4">
                          <HoverInfoLogo
                            title={
                              "Select specific items or categories eligible for this deal."
                            }
                          />
                        </div>
                      </div>
                      {error?.products && (
                        <p className="error-message text-center pt-2">
                          {error.products}
                        </p>
                      )}
                    </Grid>
                  )}

                  {hasTobaccoScanProgramAccess && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        className="qv-bogo-altria-toggle"
                        label="Altria Funded Discount"
                        control={
                          <Switch
                            checked={Boolean(+dealInfo?.altriaFundedDiscount)}
                            onChange={handleAltriaFundedDiscountToggle}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: "var(--qv-brand)",
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                {
                                  backgroundColor: "var(--qv-brand)",
                                  opacity: 1,
                                },
                            }}
                          />
                        }
                        sx={{
                          m: 0,
                          gap: "10px",
                          alignItems: "center",
                          "& .MuiFormControlLabel-label": {
                            marginLeft: 0,
                            cursor: "pointer",
                          },
                        }}
                      />
                      {Boolean(+dealInfo?.altriaFundedDiscount) && (
                        <div className="q-add-coupon-single-input mt-3">
                          <label htmlFor="promotionCode">Promotion Code</label>
                          <div
                            className="input_area"
                            style={{ marginBottom: "0px" }}
                          >
                            <BasicTextFields
                              type={"text"}
                              maxLength={20}
                              value={dealInfo.promotionCode ?? ""}
                              name="promotionCode"
                              onChangeFun={handleInputChange}
                            />
                          </div>
                          <p
                            className="mt-1"
                            style={{
                              fontSize: "12px",
                              color: "var(--qv-tx-6b7280, #6b7280)",
                            }}
                          >
                            Only needed for an Altria Loyalty Fund offer: enter it exactly as shown on
                            Altria&apos;s LFP Reporting Code Reference Sheet. Leave blank for other
                            Altria-funded promotions.
                          </p>
                        </div>
                      )}
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid
                sx={{
                  mb: 0,
                  width: "100%",
                  height: "auto",
                  boxShadow: { xs: "0 5px 10px 1px #ddd", md: "unset" },
                  borderRadius: "8px",
                  backgroundColor: "var(--qv-bg-ffffff, #fff)",
                  overflow: "hidden",
                  position: { xs: "fixed", sm: "relative" },
                  bottom: { xs: 0, md: "unset" },
                }}
                className="qv-promo-form-actions px-2 sm:px-4 py-4 flex justify-end gap-3"
              >
                <button
                  type="button"
                  className="quic-btn quic-btn-save"
                  onClick={handleAddNewDeal}
                  disabled={loading || Boolean(discountPriceConflictMessage)}
                  title={discountPriceConflictMessage || undefined}
                  style={{
                    cursor:
                      loading || discountPriceConflictMessage
                        ? "not-allowed"
                        : "pointer",
                    opacity: loading || discountPriceConflictMessage ? 0.7 : 1,
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress
                        color={"inherit"}
                        width={15}
                        size={15}
                      />{" "}
                      Add
                    </>
                  ) : (
                    "Add"
                  )}
                </button>
                <Link to={`/promotions/mix-and-match`}>
                  <button type="button" className="quic-btn quic-btn-cancle">
                    Cancel
                  </button>
                </Link>
              </Grid>
            </form>
          </div>
        </div>
      </div>

      {productModalOpen ? (
        <BogoProductSelectionModal
          open={productModalOpen}
          onClose={() => {
            setModalError("");
            startTransition(() => setProductModalOpen(false));
          }}
          onConfirm={handleProductSelectionConfirm}
          merchantId={merchant_id}
          confirmedSelections={dealInfo.products}
          activeTab={activeTab}
          discountAmount={dealInfo.discount}
          modalError={modalError}
          setModalError={setModalError}
          mode="add"
          dealType="mix_match"
        />
      ) : null}
    </>
  );
};

export default withReactQueryBoundary(AddMixAndMatchDeal);
